/**
 * Client-side Auth Service (#16)
 *
 * Manages user authentication state on the frontend.
 * Supports: email/password, OAuth (Google, GitHub), magic link.
 * In dev mode: falls back to a mock session for seamless development.
 */

// ─── Types ────────────────────────────────────────────────────

export type AuthProvider = 'email' | 'google' | 'github' | 'magic_link';

export type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url?: string;
  provider: AuthProvider;
  created_at: string;
}

export interface AuthSession {
  user: AuthUser;
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

export interface SignUpParams {
  email: string;
  password: string;
  display_name: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface OAuthParams {
  provider: 'google' | 'github';
  redirect_to?: string;
}

export interface AuthError {
  code: string;
  message: string;
}

export interface AuthResult<T = void> {
  data: T | null;
  error: AuthError | null;
}

// ─── Validation ───────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

export function validateEmail(email: string): AuthError | null {
  if (!email.trim()) return { code: 'EMPTY_EMAIL', message: 'Email is required' };
  if (!EMAIL_REGEX.test(email.trim())) return { code: 'INVALID_EMAIL', message: 'Invalid email format' };
  return null;
}

export function validatePassword(password: string): AuthError | null {
  if (!password) return { code: 'EMPTY_PASSWORD', message: 'Password is required' };
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { code: 'WEAK_PASSWORD', message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters` };
  }
  return null;
}

export function validateDisplayName(name: string): AuthError | null {
  const trimmed = name.trim();
  if (!trimmed) return { code: 'EMPTY_NAME', message: 'Display name is required' };
  if (trimmed.length < 2) return { code: 'SHORT_NAME', message: 'Display name must be at least 2 characters' };
  if (trimmed.length > 50) return { code: 'LONG_NAME', message: 'Display name must be under 50 characters' };
  return null;
}

export function validateSignUp(params: SignUpParams): AuthError[] {
  const errors: AuthError[] = [];
  const emailErr = validateEmail(params.email);
  if (emailErr) errors.push(emailErr);
  const pwErr = validatePassword(params.password);
  if (pwErr) errors.push(pwErr);
  const nameErr = validateDisplayName(params.display_name);
  if (nameErr) errors.push(nameErr);
  return errors;
}

export function validateSignIn(params: SignInParams): AuthError[] {
  const errors: AuthError[] = [];
  const emailErr = validateEmail(params.email);
  if (emailErr) errors.push(emailErr);
  if (!params.password) errors.push({ code: 'EMPTY_PASSWORD', message: 'Password is required' });
  return errors;
}

// ─── Session Management ───────────────────────────────────────

const SESSION_KEY = 'ideagraph_auth_session';

export function saveSession(session: AuthSession): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // localStorage might be full or disabled
  }
}

export function loadSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: AuthSession = JSON.parse(raw);
    // Check expiration
    if (session.expires_at < Date.now()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export function isSessionExpired(session: AuthSession): boolean {
  return session.expires_at < Date.now();
}

export function shouldRefreshSession(session: AuthSession): boolean {
  // Refresh if less than 5 minutes remaining
  const FIVE_MINUTES = 5 * 60 * 1000;
  return session.expires_at - Date.now() < FIVE_MINUTES;
}

// ─── Mock Auth (Dev Mode) ─────────────────────────────────────

const MOCK_USER: AuthUser = {
  id: 'mock-user-001',
  email: 'demo@ideagraph.dev',
  display_name: 'Demo User',
  avatar_url: undefined,
  provider: 'email',
  created_at: '2026-01-01T00:00:00Z',
};

export function createMockSession(): AuthSession {
  return {
    user: MOCK_USER,
    access_token: 'mock-token-' + Date.now(),
    refresh_token: 'mock-refresh-' + Date.now(),
    expires_at: Date.now() + 3600_000, // 1 hour
  };
}

export function isMockMode(): boolean {
  // Use globalThis to avoid direct `process` reference that fails in browser bundlers
  const g = globalThis as Record<string, unknown>;
  const proc = g['process'] as { env?: Record<string, string | undefined> } | undefined;
  if (proc?.env) {
    // Check Vite env first, fallback to legacy Next.js env
    const supabaseUrl = proc.env['VITE_SUPABASE_URL'];
    return !supabaseUrl || proc.env['NODE_ENV'] === 'test';
  }
  return true;
}

// ─── Auth State Machine ───────────────────────────────────────

export interface AuthStore {
  state: AuthState;
  session: AuthSession | null;
  errors: AuthError[];
}

export function getInitialAuthStore(): AuthStore {
  return {
    state: 'loading',
    session: null,
    errors: [],
  };
}

/**
 * Derive display info from auth state
 */
export function getAuthDisplayInfo(store: AuthStore): {
  isLoading: boolean;
  isAuthenticated: boolean;
  userName: string;
  userEmail: string;
  avatarUrl?: string;
} {
  return {
    isLoading: store.state === 'loading',
    isAuthenticated: store.state === 'authenticated',
    userName: store.session?.user.display_name ?? 'Guest',
    userEmail: store.session?.user.email ?? '',
    avatarUrl: store.session?.user.avatar_url,
  };
}

/**
 * Get the auth header value for API calls
 */
export function getAuthHeader(store: AuthStore): Record<string, string> {
  if (!store.session?.access_token) return {};
  return { Authorization: `Bearer ${store.session.access_token}` };
}

// ─── OAuth URL Builder ────────────────────────────────────────

export function buildOAuthUrl(params: OAuthParams): string {
  // Use globalThis to avoid direct `process` reference that fails in browser bundlers
  const g = globalThis as Record<string, unknown>;
  const proc = g['process'] as { env?: Record<string, string | undefined> } | undefined;
  // Check Vite env first, fallback to legacy Next.js env
  const base = proc?.env?.['VITE_SUPABASE_URL'] ?? '';
  const redirectTo = params.redirect_to ?? (typeof window !== 'undefined' ? window.location.origin + '/auth/callback' : '/auth/callback');
  return `${base}/auth/v1/authorize?provider=${params.provider}&redirect_to=${encodeURIComponent(redirectTo)}`;
}
